import { Runtime } from "fp";

import { Order } from "~/Order";
import { Search } from "~/Search";
import { Version } from "~/Version";

import { Application as Application_ } from "./Application";
import { Platform as Platform_ } from "./Platform";

export type Device = {
  readonly device: Partial<Device.Application & Device.Platform>;
};

export namespace Device {
  export import Application = Application_;
  export import Platform = Platform_;

  export const { equals, compare } = Order.fromCompare<Device>(
    (a, b) =>
      Order.optionally(Application.compare)(
        a.device.application && { application: a.device.application },
        b.device.application && { application: b.device.application }
      ) ||
      Order.optionally(Platform.compare)(
        a.device.platform && { platform: a.device.platform },
        b.device.platform && { platform: b.device.platform }
      )
  );

  export const isOutdated = ({
    device,
  }: Device): Partial<Version> & {
    readonly latest?: Version;
    readonly isOutdated: boolean;
  } => {
    if (!device?.platform)
      return { ...device.application, isOutdated: true };

    const platform = { platform: device.platform };
    const latest = Application.Latest.fromPlatform(platform);

    if (!device?.application) return { latest, isOutdated: true };

    const application = device.application;
    const isOutdated = Application.isOutdated(latest)({ application });

    return { ...application, latest, isOutdated };
  };

  export const toSearch: Search.From<Device> = ({
    device: { application, platform },
  }) =>
    Search.join(
      application ? Application.toSearch({ application }) : Search.empty(),
      platform ? Platform.toSearch({ platform }) : Search.empty()
    );

  export namespace Response {
    export const runtime = () =>
      Runtime.intersection([
        Application.Response.runtime(),
        Platform.Response.runtime(),
      ]);

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Device | undefined => {
      const application = Application.Response.decode(response);
      const platform = Platform.Response.decode(response);
      return (
        (application || platform) && { device: { ...application, ...platform } }
      );
    };
  }
}
